import { CalendarView } from "@/components/calendars/CalendarView.tsx";
import { Button } from "@/components/ui/button.tsx";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox.tsx";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog.tsx";
import { Label } from "@/components/ui/label.tsx";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select.tsx";
import { parseISOToDate, parseISOToMs } from "@/lib/date-utils.ts";
import { cn } from "@/lib/utils.ts";
import type { DateSelectArg, EventInput } from "@fullcalendar/core";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

const DEFAULT_TITLE = "Pick your availability";
const DEFAULT_DESCRIPTION =
  "Drag empty space to add. Drag from a range's edge to resize it (toward the middle to shrink, away to grow). Drag past the far edge to remove. Double-click a range to remove it.";

interface CalendarAvailabilityPickerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialDate: Date;
  duration: number;
  initialRanges?: Array<{ startISO: string; endISO: string }>;
  initialSkipCalendarConflicts?: boolean;
  onSave: (
    ranges: Array<{ startISO: string; endISO: string }>,
    skipCalendarConflicts: boolean,
    durationMinutes: number,
  ) => void;
  /**
   * Proposed-slots mode only: show an in-picker meeting-duration selector.
   * Changing it re-sizes every slot to the new length; the chosen length comes
   * back through `onSave` so the caller can persist duration + slots together.
   */
  editableDuration?: boolean;
  /** Notified live when the duration selector changes (optional). */
  onDurationChange?: (minutes: number) => void;
  /** Durations (minutes) offered by the selector. */
  durationOptions?: number[];
  /** Dialog heading. Defaults to the availability-picking copy. */
  title?: string;
  /** Dialog description. Defaults to the availability-picking copy. */
  description?: string;
  /** Show the "Ignore calendar conflicts" checkbox (availability mode only). */
  showSkipConflictsCheckbox?: boolean;
  /**
   * Proposed-slots mode only: when true (default), the first drag clears
   * `initialRanges` and starts fresh — wrong for editing a concrete
   * proposed-times list, where the existing slots should be adjusted, not
   * wiped, so the proposed-slots editor passes false. Has no effect in
   * availability mode (its first-create-replaces behavior is unconditional).
   */
  replaceOnFirstEdit?: boolean;
  /**
   * When set, drag-created and dragged/resized ranges are forced to this
   * length in minutes — fixed-duration proposed meeting slots.
   */
  snapDurationMinutes?: number;
}

interface SelectedRange {
  id: string;
  startISO: string;
  endISO: string;
}

function rangeId(startISO: string, endISO: string) {
  return `${startISO}__${endISO}`;
}

const SLOT_DURATION_MS = 30 * 60 * 1000;

/** Merge any overlapping or adjacent ranges into a canonical sorted list. */
function unionRanges(ranges: SelectedRange[]): SelectedRange[] {
  if (ranges.length <= 1) return ranges;
  const sorted = [...ranges].sort((a, b) => parseISOToMs(a.startISO) - parseISOToMs(b.startISO));
  const result: SelectedRange[] = [];
  let curStartISO = sorted[0].startISO;
  let curEndMs = parseISOToMs(sorted[0].endISO);
  for (let i = 1; i < sorted.length; i++) {
    const next = sorted[i];
    const nextStartMs = parseISOToMs(next.startISO);
    const nextEndMs = parseISOToMs(next.endISO);
    if (nextStartMs <= curEndMs) {
      if (nextEndMs > curEndMs) curEndMs = nextEndMs;
    } else {
      const endISO = new Date(curEndMs).toISOString();
      result.push({ id: rangeId(curStartISO, endISO), startISO: curStartISO, endISO });
      curStartISO = next.startISO;
      curEndMs = nextEndMs;
    }
  }
  const endISO = new Date(curEndMs).toISOString();
  result.push({ id: rangeId(curStartISO, endISO), startISO: curStartISO, endISO });
  return result;
}

export function CalendarAvailabilityPicker({
  open,
  onOpenChange,
  initialDate,
  duration: _duration,
  initialRanges,
  initialSkipCalendarConflicts = true,
  onSave,
  title = DEFAULT_TITLE,
  description = DEFAULT_DESCRIPTION,
  showSkipConflictsCheckbox = true,
  replaceOnFirstEdit = true,
  snapDurationMinutes,
  editableDuration = false,
  onDurationChange,
  durationOptions = [15, 30, 45, 60, 90],
}: CalendarAvailabilityPickerProps) {
  const [currentDate, setCurrentDate] = useState(initialDate);
  // Editable meeting length (proposed-slots mode). Seeded from the snap prop;
  // the selector below mutates it and re-sizes the slots. `snapMin` (below) is
  // the live length used by every snap calculation.
  const [durationMin, setDurationMin] = useState(snapDurationMinutes);
  const [selectedRanges, setSelectedRanges] = useState<SelectedRange[]>(() =>
    (initialRanges ?? []).map((r) => ({
      id: rangeId(r.startISO, r.endISO),
      startISO: r.startISO,
      endISO: r.endISO,
    })),
  );
  const [skipConflicts, setSkipConflicts] = useState(initialSkipCalendarConflicts);
  // Mirror of selectedRanges for reading the current list inside event handlers
  // without a stale render closure (e.g. the drag-collision check, which also
  // must run outside a setState updater so it can call FullCalendar's revert()).
  const selectedRangesRef = useRef(selectedRanges);
  selectedRangesRef.current = selectedRanges;
  const wasOpenRef = useRef(false);
  const hasEditedRef = useRef(false);
  // Where the user pressed down — captured from CalendarView's onSelectStart on
  // the first selectAllow of a drag. Lets handleSelect tell resize (drag started
  // inside an existing range) from create (drag started on empty space), which
  // the normalized `onSelect` start/end cannot reveal.
  const dragStartMsRef = useRef<number | null>(null);
  const lastRangeClickRef = useRef<{ rangeId: string; time: number } | null>(null);
  const lastToggleAtRef = useRef(0);

  useEffect(() => {
    if (open && !wasOpenRef.current) {
      setCurrentDate(initialDate);
      setSelectedRanges(
        (initialRanges ?? []).map((r) => ({
          id: rangeId(r.startISO, r.endISO),
          startISO: r.startISO,
          endISO: r.endISO,
        })),
      );
      setSkipConflicts(initialSkipCalendarConflicts);
      setDurationMin(snapDurationMinutes);
      hasEditedRef.current = false;
    }
    wasOpenRef.current = open;
  }, [open, initialDate, initialRanges, initialSkipCalendarConflicts, snapDurationMinutes]);

  // Live meeting length used by the snap math — the editable selection when the
  // duration selector is on, else the fixed prop.
  const snapMin = durationMin ?? snapDurationMinutes;

  // Change the meeting length: re-size every slot to start + new length
  // (anchored at each start), then notify the caller. Patched on Save alongside
  // the slots, so the artifact never carries a duration that disagrees with the
  // slot lengths.
  const handleDurationChange = useCallback(
    (minutes: number) => {
      setDurationMin(minutes);
      hasEditedRef.current = true;
      setSelectedRanges((prev) =>
        prev.map((r) => {
          const startMs = parseISOToMs(r.startISO);
          const endISO = new Date(startMs + minutes * 60_000).toISOString();
          return { id: rangeId(r.startISO, endISO), startISO: r.startISO, endISO };
        }),
      );
      onDurationChange?.(minutes);
    },
    [onDurationChange],
  );

  // Proposed-slot edit mode (snap-duration is set): render ranges as
  // editable foreground events so they can be dragged/resized on the
  // calendar. Availability mode keeps the legacy background-event style
  // (drag-to-add, click-to-remove); `availability-selected` in calendar.css
  // sets pointer-events:none there so taps/drags fall through to the cell.
  const isProposedSlotsMode = snapDurationMinutes !== undefined;

  const additionalEvents: EventInput[] = useMemo(
    () =>
      selectedRanges.map((r) => {
        const base: EventInput = {
          id: r.id,
          start: parseISOToDate(r.startISO),
          end: parseISOToDate(r.endISO),
          backgroundColor: "rgb(16 185 129 / 0.3)",
          borderColor: "rgb(16 185 129)",
          classNames: ["availability-selected"],
          extendedProps: { isSelectedAvailability: true },
        };
        return isProposedSlotsMode
          ? // Proposed slots are draggable (move) but fixed-duration, so
            // duration editing is off — the bottom-edge resize handle would be
            // misleading since handleEventChange snaps end back to start+duration.
            { ...base, editable: true, durationEditable: false }
          : { ...base, display: "background" as const };
      }),
    [selectedRanges, isProposedSlotsMode],
  );

  const handleSelectStart = useCallback((span: { start: Date; end: Date }) => {
    dragStartMsRef.current = span.start.getTime();
  }, []);

  // Center the calendar on the proposed-slot times when the dialog opens
  // — proposed slots typically cluster in the work day, but the kickoff's
  // roughTimeRange may anchor on midnight, leaving the user to scroll to
  // find the slots. Compute the earliest slot start minus a 2-hour
  // buffer so the slot isn't flush against the top edge. Recomputed
  // each time `initialRanges` changes (cheap memo).
  const initialScrollTime = useMemo(() => {
    if (!initialRanges || initialRanges.length === 0) return undefined;
    const earliest = initialRanges.reduce((min, r) => {
      const t = parseISOToMs(r.startISO);
      return t < min ? t : min;
    }, Number.POSITIVE_INFINITY);
    if (!Number.isFinite(earliest)) return undefined;
    const buffered = new Date(earliest - 2 * 60 * 60 * 1000);
    const hh = String(buffered.getHours()).padStart(2, "0");
    const mm = String(buffered.getMinutes()).padStart(2, "0");
    return `${hh}:${mm}`;
  }, [initialRanges]);

  // Proposed-slots add/remove, shared by both gestures that can land on empty
  // space — the drag (`handleProposedSlotSelect`) and the click
  // (`handleProposedSlotClick`) — so the two can never drift on the rules.
  // Existing slots are moved/resized by dragging their event elements
  // (handleEventChange). On first edit when showing defaults, clear defaults and
  // start fresh (only when `replaceOnFirstEdit` — proposed-times editing keeps
  // the slot list instead).
  const toggleProposedSlot = useCallback(
    (newStart: number, newEnd: number) => {
      const startISO = new Date(newStart).toISOString();
      const endISO = new Date(newEnd).toISOString();

      // First edit clears defaults — start fresh with just the new selection.
      // Proposed-slot editors pass replaceOnFirstEdit={false} so their AI-
      // generated slot list is preserved and the new slot appends.
      if (replaceOnFirstEdit && !hasEditedRef.current) {
        hasEditedRef.current = true;
        setSelectedRanges([{ id: rangeId(startISO, endISO), startISO, endISO }]);
        return;
      }
      hasEditedRef.current = true;

      // Proposed slots are DISCRETE fixed-duration blocks, not a continuous
      // availability region — so we toggle whole slots rather than doing
      // interval subtraction (which could leave sub-duration fragments or
      // overlapping duplicates). Exact match → remove (toggle off); any other
      // overlap → no-op (don't fragment or duplicate); otherwise → add.
      setSelectedRanges((prev) => {
        const exactMatch = prev.find(
          (r) => parseISOToMs(r.startISO) === newStart && parseISOToMs(r.endISO) === newEnd,
        );
        if (exactMatch) {
          return prev.filter((r) => r.id !== exactMatch.id);
        }
        const overlaps = prev.some((r) => {
          const rStart = parseISOToMs(r.startISO);
          const rEnd = parseISOToMs(r.endISO);
          return newStart < rEnd && newEnd > rStart;
        });
        if (overlaps) {
          // Partial overlap with a different slot — ignore the gesture rather
          // than create a fragment or a near-duplicate.
          return prev;
        }
        return [...prev, { id: rangeId(startISO, endISO), startISO, endISO }];
      });
    },
    [replaceOnFirstEdit],
  );

  // Proposed-slots drag gesture (dispatched from handleSelect when
  // isProposedSlotsMode): adds a slot snapped to `snapDurationMinutes`, however
  // far the drag actually reached.
  const handleProposedSlotSelect = useCallback(
    (selectInfo: DateSelectArg) => {
      // Stamped so the click handler can tell a `dateClick` that belongs to this
      // same gesture from a fresh one — see handleProposedSlotClick.
      lastToggleAtRef.current = Date.now();
      const newStart = selectInfo.start.getTime();
      toggleProposedSlot(
        newStart,
        snapMin ? newStart + snapMin * 60_000 : selectInfo.end.getTime(),
      );
    },
    [snapMin, toggleProposedSlot],
  );

  // Proposed-slots click gesture: a single click on empty space proposes one
  // slot of the meeting's length there — click 5pm on a 30-minute meeting and
  // you get 5:00–5:30. Only a drag used to add (FullCalendar's
  // `selectMinDistance: 5` means a click never fires `select`), which read as
  // the picker ignoring the click entirely (BIG-5708).
  //
  // Clicks on an existing slot don't arrive here: proposed slots render as
  // foreground events, so they take the click themselves and
  // `handleRawEventClick` removes them.
  const handleProposedSlotClick = useCallback(
    (info: { date: Date; allDay: boolean }) => {
      // The all-day row carries no time of day to anchor a meeting slot to.
      if (info.allDay) return;
      // A press-and-hold release can emit both `select` and `dateClick` for one
      // gesture; without this the second toggle would undo the first. Same
      // window the availability path uses.
      if (Date.now() - lastToggleAtRef.current < 100) return;
      lastToggleAtRef.current = Date.now();
      const newStart = info.date.getTime();
      toggleProposedSlot(newStart, newStart + (snapMin ? snapMin * 60_000 : SLOT_DURATION_MS));
    },
    [snapMin, toggleProposedSlot],
  );

  /**
   * WeeklyTimePicker-style edge-drag resize.
   *
   * If the drag started inside an existing range, that range is RESIZED. The
   * pivot is the far edge of the range from the click cell; the near edge
   * follows the pointer. So:
   *   - Click the last cell + drag toward the middle  → bottom shrinks
   *   - Click the last cell + drag away from middle   → bottom grows
   *   - Click the first cell + drag toward middle     → top shrinks
   *   - Click the first cell + drag away from middle  → top grows
   *   - Drag the near edge past the pivot             → range collapses, removed
   *
   * If the drag started in empty space, a new range is added (union-merged
   * with any range it overlaps).
   *
   * FC reports the drag span as cell *boundaries*, not pointer pixel
   * positions — using `select.end` directly as "where the pointer is" would
   * overshoot by one slot. Reconstruct the pointer's cell start from drag
   * direction:
   *   - drag went down (dragStart === newStart) → pointer at newEnd - slot
   *   - drag went up                            → pointer at newStart
   */
  const handleSelect = useCallback(
    (info: DateSelectArg) => {
      // Proposed-slots mode adds fixed-duration slots via its own snap gesture;
      // availability mode uses the edge-drag-resize model below.
      if (isProposedSlotsMode) {
        handleProposedSlotSelect(info);
        return;
      }
      if (Date.now() - lastToggleAtRef.current < 100) {
        dragStartMsRef.current = null;
        return;
      }
      lastToggleAtRef.current = Date.now();

      const newStartMs = info.start.getTime();
      const newEndMs = info.end.getTime();
      const dragStartMs = dragStartMsRef.current ?? newStartMs;
      dragStartMsRef.current = null;

      // Read state via the closure so the updater stays pure — React StrictMode
      // double-invokes functional updaters, and ref mutations inside them
      // produce off-by-one effects (e.g. flipping `hasEditedRef` between the
      // two invocations changes which branch the second pass takes).
      const existingRange = selectedRanges.find((r) => {
        const rs = parseISOToMs(r.startISO);
        const re = parseISOToMs(r.endISO);
        return dragStartMs >= rs && dragStartMs < re;
      });

      // First create-gesture clears the defaults. Editing a default keeps the
      // others. Side effect lives out here, not in the updater.
      const isFirstCreate = !hasEditedRef.current && !existingRange;
      hasEditedRef.current = true;

      if (isFirstCreate) {
        const startISO = info.start.toISOString();
        const endISO = info.end.toISOString();
        setSelectedRanges([{ id: rangeId(startISO, endISO), startISO, endISO }]);
        return;
      }

      if (!existingRange) {
        const startISO = info.start.toISOString();
        const endISO = info.end.toISOString();
        const newRange: SelectedRange = { id: rangeId(startISO, endISO), startISO, endISO };
        setSelectedRanges((prev) => unionRanges([...prev, newRange]));
        return;
      }

      const rs = parseISOToMs(existingRange.startISO);
      const re = parseISOToMs(existingRange.endISO);
      const distToStart = dragStartMs - rs;
      const distToEnd = re - dragStartMs;
      // Pivot = far edge, so near edge follows pointer. Ties resolve to end
      // (matches WeeklyTimePicker).
      const pivot = distToEnd >= distToStart ? re : rs;

      const dragWentDown = dragStartMs === newStartMs;
      const pointerCellStart = dragWentDown ? newEndMs - SLOT_DURATION_MS : newStartMs;

      // The active (near) edge follows the pointer cell. Top edge sits at the
      // pointer cell's START boundary; bottom edge sits at its END.
      const activeEdgeMs = pivot === re ? pointerCellStart : pointerCellStart + SLOT_DURATION_MS;

      // Drag the active edge to or past the pivot → range collapses, remove.
      const clickBeforePivot = dragStartMs < pivot;
      const pastOrAtPivot = clickBeforePivot ? activeEdgeMs >= pivot : activeEdgeMs <= pivot;
      if (pastOrAtPivot) {
        setSelectedRanges((prev) => prev.filter((r) => r.id !== existingRange.id));
        return;
      }

      const finalStart = Math.min(pivot, activeEdgeMs);
      const finalEnd = Math.max(pivot, activeEdgeMs);
      const newStartISO = new Date(finalStart).toISOString();
      const newEndISO = new Date(finalEnd).toISOString();
      setSelectedRanges((prev) =>
        unionRanges(
          prev.map((r) =>
            r.id === existingRange.id
              ? { id: rangeId(newStartISO, newEndISO), startISO: newStartISO, endISO: newEndISO }
              : r,
          ),
        ),
      );
    },
    [selectedRanges, isProposedSlotsMode, handleProposedSlotSelect],
  );

  // Single tap on a cell: no-op for empty cells; on selected cells, start
  // double-tap detection — a second tap within 400ms removes the range.
  // All ref mutations happen here, not inside the updater — StrictMode
  // double-invokes updaters and would treat the first tap as a double-tap.
  const handleDateClick = useCallback(
    (info: { date: Date; allDay: boolean }) => {
      if (Date.now() - lastToggleAtRef.current < 100) return;
      lastToggleAtRef.current = Date.now();

      const clickMs = info.date.getTime();
      const range = selectedRanges.find((r) => {
        const rs = parseISOToMs(r.startISO);
        const re = parseISOToMs(r.endISO);
        return clickMs >= rs && clickMs < re;
      });
      if (!range) {
        lastRangeClickRef.current = null;
        return;
      }
      const now = Date.now();
      const last = lastRangeClickRef.current;
      if (last && last.rangeId === range.id && now - last.time < 400) {
        lastRangeClickRef.current = null;
        hasEditedRef.current = true;
        setSelectedRanges((prev) => prev.filter((r) => r.id !== range.id));
        return;
      }
      lastRangeClickRef.current = { rangeId: range.id, time: now };
    },
    [selectedRanges],
  );

  // Drag-to-move / resize handler. Only wired in proposed-slots mode; the
  // event is replaced in `selectedRanges` with the new start. When
  // `snapDurationMinutes` is set, end is forced to `start + duration` so
  // proposed slots stay a fixed length regardless of how the user
  // resized them.
  const handleEventChange = useCallback(
    (info: { event: { id: string; start: Date | null; end: Date | null }; revert: () => void }) => {
      if (!info.event.start) {
        info.revert();
        return;
      }
      const newStart = info.event.start;
      const newEnd =
        snapMin !== undefined
          ? new Date(newStart.getTime() + snapMin * 60_000)
          : (info.event.end ?? newStart);
      const startISO = newStart.toISOString();
      const endISO = newEnd.toISOString();
      const newId = rangeId(startISO, endISO);
      const newStartMs = newStart.getTime();
      const newEndMs = newEnd.getTime();

      // Reject a move that would overlap a DIFFERENT slot — otherwise two slots
      // could stack (and an exact landing would mint a duplicate rangeId /
      // FullCalendar event id). FullCalendar keeps the event at the dropped
      // position and won't reconcile to props mid-drag, so we must call
      // `revert()` to snap it back; just leaving state unchanged isn't enough.
      const collides = selectedRangesRef.current.some((r) => {
        if (r.id === info.event.id) return false;
        const rStart = parseISOToMs(r.startISO);
        const rEnd = parseISOToMs(r.endISO);
        return newStartMs < rEnd && newEndMs > rStart;
      });
      if (collides) {
        info.revert();
        return;
      }

      hasEditedRef.current = true;
      setSelectedRanges((prev) =>
        prev.map((r) => (r.id === info.event.id ? { id: newId, startISO, endISO } : r)),
      );
    },
    [snapMin],
  );

  // Click a proposed-slot foreground event to remove it. Availability-mode
  // ranges are background events (pointer-events:none), so this only fires for
  // the editable proposed slots, which carry `isSelectedAvailability`.
  const handleRawEventClick = useCallback(
    (info: { event: { id: string; extendedProps: Record<string, unknown> } }) => {
      if (info.event.extendedProps["isSelectedAvailability"]) {
        hasEditedRef.current = true;
        setSelectedRanges((prev) => prev.filter((r) => r.id !== info.event.id));
      }
    },
    [],
  );

  const handleDateChange = useCallback((date: Date) => {
    setCurrentDate(date);
  }, []);

  // Week navigation. Some kickoff proposals span multiple weeks (e.g.
  // "anytime next week" picks Mon/Tue of week A and Wed/Thu of week B),
  // and the picker shows one week at a time. Prev/next buttons let the
  // user move the visible window without leaving the dialog.
  const goToPrevWeek = useCallback(() => {
    setCurrentDate((d) => new Date(d.getTime() - 7 * 24 * 60 * 60 * 1000));
  }, []);
  const goToNextWeek = useCallback(() => {
    setCurrentDate((d) => new Date(d.getTime() + 7 * 24 * 60 * 60 * 1000));
  }, []);

  // Format the visible week's range as "Jun 1 – Jun 7" for the label.
  const weekLabel = useMemo(() => {
    const dayOfWeek = currentDate.getDay();
    // Anchor to the Sunday of the displayed week (FullCalendar's week
    // view starts on Sunday by default).
    const start = new Date(currentDate);
    start.setDate(start.getDate() - dayOfWeek);
    const end = new Date(start);
    end.setDate(end.getDate() + 6);
    const fmt = (d: Date) => d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
    return `${fmt(start)} – ${fmt(end)}`;
  }, [currentDate]);

  const handleSave = () => {
    onSave(
      selectedRanges.map((r) => ({ startISO: r.startISO, endISO: r.endISO })),
      skipConflicts,
      snapMin ?? _duration,
    );
    onOpenChange(false);
  };

  const handleClear = () => {
    setSelectedRanges([]);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange} modal>
      <DialogContent
        className="max-w-4xl h-[80vh] flex flex-col p-0"
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        onFocusOutside={(e) => e.preventDefault()}
      >
        <DialogHeader className="px-6 pt-6 pb-2">
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        {/* Week navigation — proposal time ranges often span multiple
            weeks; prev/next lets the user move the visible window
            without leaving the dialog. */}
        <div className="flex items-center justify-between px-6 pb-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={goToPrevWeek}
            data-statsig-name="availability-picker-prev-week"
          >
            <ChevronLeft className="size-4 mr-1" />
            Previous week
          </Button>
          <span className="text-sm text-muted-foreground">{weekLabel}</span>
          <Button
            variant="ghost"
            size="sm"
            onClick={goToNextWeek}
            data-statsig-name="availability-picker-next-week"
          >
            Next week
            <ChevronRight className="size-4 ml-1" />
          </Button>
        </div>

        {/* Calendar using existing CalendarView */}
        <div className="flex-1 min-h-0 px-6 overflow-hidden flex flex-col">
          <CalendarView
            viewType="week"
            currentDate={currentDate}
            onDateChange={handleDateChange}
            initialScrollTime={initialScrollTime}
            selectable
            onSelect={handleSelect}
            onSelectStart={handleSelectStart}
            // Two different click meanings: in proposed-slots mode a click on
            // empty space adds a slot (removal is a click on the slot's own
            // event, via onRawEventClick); in availability mode a click only
            // arms double-tap-to-remove on an already-selected cell.
            onDateClick={isProposedSlotsMode ? handleProposedSlotClick : handleDateClick}
            additionalEvents={additionalEvents}
            onRawEventClick={handleRawEventClick}
            onEventChange={isProposedSlotsMode ? handleEventChange : undefined}
            slotDuration="00:30:00"
            // The row stays 30 minutes tall, but a gesture can land on its
            // halves: click the lower half of the 12:00 row and the meeting is
            // proposed at 12:15, not rounded back to 12:00 (BIG-5706).
            // Availability mode keeps the 30-minute snap its edge-drag resize
            // math is written against.
            snapDuration={isProposedSlotsMode ? "00:15:00" : undefined}
            // Real calendar events are read-only context here — don't open
            // Google Calendar when one is clicked.
            disableEventLinks
          />
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t">
          <div className="flex items-center gap-4">
            {editableDuration && snapMin !== undefined && (
              <div className="flex items-center gap-2">
                <Label htmlFor="meeting-duration" className="text-sm">
                  Duration
                </Label>
                <Select
                  value={String(snapMin ?? _duration)}
                  onValueChange={(v) => handleDurationChange(Number(v))}
                >
                  <SelectTrigger id="meeting-duration" className="h-8 w-28">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {durationOptions.map((min) => (
                      <SelectItem key={min} value={String(min)}>
                        {min} min
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {showSkipConflictsCheckbox && (
              <div className="flex items-center gap-2">
                <Checkbox
                  id="ignore-conflicts"
                  checked={skipConflicts}
                  onCheckedChange={(checked) => setSkipConflicts(checked === true)}
                />
                <Label htmlFor="ignore-conflicts" className="text-sm cursor-pointer">
                  Ignore calendar conflicts
                </Label>
              </div>
            )}
            <span
              className={cn(
                "text-xs",
                selectedRanges.length > 0 ? "text-muted-foreground" : "text-muted-foreground/50",
              )}
            >
              {selectedRanges.length} {selectedRanges.length === 1 ? "slot" : "slots"} selected
            </span>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleClear}
              disabled={selectedRanges.length === 0}
              data-statsig-name="availability-picker-clear"
            >
              Clear
            </Button>
            <Button size="sm" onClick={handleSave} data-statsig-name="availability-picker-save">
              Save
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
